// frontend/dispatcher-client/src/components/PurchaseOrderPrint.js
import React, { forwardRef } from "react";
import "../styles/PurchaseOrderPrint.css";

/* ========= Helpers ========= */
const LOCALE = "en-GB";
const DEFAULT_CURRENCY = "EUR";

const safe = (v, fallback = "—") =>
  v === 0 ? "0" : v == null || v === "" ? fallback : v;

const makeCurrencyFormatter = (currencyCode = DEFAULT_CURRENCY) =>
  typeof Intl !== "undefined"
    ? new Intl.NumberFormat(LOCALE, {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 2,
      })
    : null;

const nfDate =
  typeof Intl !== "undefined"
    ? new Intl.DateTimeFormat(LOCALE, {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;

const fmtDate = (d) => {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "—";
  return nfDate ? nfDate.format(date) : date.toLocaleDateString();
};

const toNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const PurchaseOrderPrint = forwardRef(
  ({ purchaseOrder = {}, issuer = {}, supplier = {}, company }, ref) => {
    const issuerIn = issuer || company || {};

    const {
        poNumber,
        date,
        paymentDueDate,
        currency = DEFAULT_CURRENCY,
        items,
        vatRate,
        discount,
        paymentTerms,
        deliveryAddress,
        notes,
        reference,
        pickup,
        delivery,
        price4subcontrated,
        price,
        total: totalFromPo,
      } = purchaseOrder;

    const nfCurrency = makeCurrencyFormatter(currency);

    const money = (v) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return nfCurrency ? nfCurrency.format(n) : `${n.toFixed(2)} ${currency}`;
    };

    const fallbackAmount = toNumber(
        price4subcontrated ?? price ?? totalFromPo ?? 0,
        0
      );
  
      const lines =
        Array.isArray(items) && items.length
          ? items.map((item, idx) => {
              const qty = toNumber(item.qty ?? item.quantity ?? 1, 1);
              const unitPrice = toNumber(
                item.unitPrice ?? item.price ?? item.unit ?? item.amount ?? 0,
                0
              );
              const total =
                item.total != null
                  ? toNumber(item.total, 0)
                  : qty * unitPrice;
  
              return {
                id: item.id ?? idx,
                description: safe(item.description ?? item.name, "—"),
                qty,
                unitPrice,
                total,
              };
            })
          : [
              {
                id: "fallback-1",
                description: `Subcontracted transport ${safe(pickup)} → ${safe(delivery)}`,
                qty: 1,
                unitPrice: fallbackAmount,
                total: fallbackAmount,
              },
            ];

    const subtotal = lines.reduce((sum, line) => sum + toNumber(line.total), 0);
    const vatPct = Number.isFinite(Number(vatRate)) ? Number(vatRate) : 0;
    const vat = subtotal * (vatPct / 100);
    const disc = toNumber(discount, 0);
    const total = subtotal - disc + vat;

    const dateOut = fmtDate(date);
    const dueDateOut = fmtDate(paymentDueDate);

    const fmtEmail = (v) => (v ? String(v).trim() : "");
    const fmtPhone = (v) => (v ? String(v).trim() : "");

    const iName = safe(issuerIn.name, "Company Name");
    const iAddr = safe(issuerIn.address, "");
    const iCityLine = [
      safe(issuerIn.postalCode, ""),
      safe(issuerIn.city, ""),
      safe(issuerIn.country, ""),
    ]
      .filter(Boolean)
      .join(", ");
    const iPhoneEmail = [fmtPhone(issuerIn.phone), fmtEmail(issuerIn.email)]
      .filter(Boolean)
      .join(" | ");

    const sName = safe(supplier?.name, "Supplier Name");
    const sAddr = safe(supplier?.address, "");
    const sCityLine = [
      safe(supplier?.postalCode, ""),
      safe(supplier?.city, ""),
      safe(supplier?.country, ""),
    ]
      .filter(Boolean)
      .join(", ");
    const sPhoneEmail = [fmtPhone(supplier?.phone), fmtEmail(supplier?.email)]
      .filter(Boolean)
      .join(" | ");

    return (
      <div className="purchase-order-print-sheetA4 purchase-order-print-neoBg" ref={ref}>
        <header className="purchase-order-print-headerSimple">
          <div className="purchase-order-print-logoRow">
            {issuerIn.logoUrl ? (
              <img
                src={issuerIn.logoUrl}
                alt="logo"
                className="purchase-order-print-headLogo"
                crossOrigin="anonymous"
              />
            ) : (
              <div className="purchase-order-print-headLogoPh">LOGO</div>
            )}
          </div>

          <div className="purchase-order-print-twoCol">
            <div className="purchase-order-print-leftCol">
              <div className="purchase-order-print-companyBlock purchase-order-print-companyBlockIssuer">
                <div className="purchase-order-print-companyName">{iName}</div>
                {iAddr && <div className="purchase-order-print-muted">{iAddr}</div>}
                {iCityLine && (
                  <div className="purchase-order-print-muted">{iCityLine}</div>
                )}
                {iPhoneEmail && (
                  <div className="purchase-order-print-muted">{iPhoneEmail}</div>
                )}
              </div>

              <div className="purchase-order-print-metaPlain">
                <div className="purchase-order-print-metaTitle">Purchase Order</div>

                <div className="purchase-order-print-metaRow">
                  <span className="purchase-order-print-label">PO Number:</span>
                  <span className="purchase-order-print-value">
                    {safe(poNumber)}
                  </span>
                </div>

                <div className="purchase-order-print-metaRow">
                  <span className="purchase-order-print-label">Issue Date:</span>
                  <span className="purchase-order-print-value">
                    {dateOut === "—" ? "DD/MM/YYYY" : dateOut}
                  </span>
                </div>

                <div className="purchase-order-print-metaRow">
                  <span className="purchase-order-print-label">
                    Due Date:
                  </span>
                  <span className="purchase-order-print-value">
                    {dueDateOut === "—" ? "DD/MM/YYYY" : dueDateOut}
                  </span>
                </div>

                {reference ? (
                  <div className="purchase-order-print-metaRow">
                    <span className="purchase-order-print-label">Reference:</span>
                    <span className="purchase-order-print-value">
                      {safe(reference)}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="purchase-order-print-rightCol">
              <div className="purchase-order-print-billToCard">
                <div className="purchase-order-print-billToLabel">Supplier</div>

                <div className="purchase-order-print-companyBlock purchase-order-print-companyBlockClient">
                  <div className="purchase-order-print-companyName">{sName}</div>
                  {sAddr && <div className="purchase-order-print-muted">{sAddr}</div>}
                  {sCityLine && (
                    <div className="purchase-order-print-muted">{sCityLine}</div>
                  )}
                  {sPhoneEmail && (
                    <div className="purchase-order-print-muted">{sPhoneEmail}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        <table className="purchase-order-print-gridTable">
          <thead>
            <tr>
              <th className="purchase-order-print-colQty">QTY</th>
              <th className="purchase-order-print-colDesc">DESCRIPTION</th>
              <th className="purchase-order-print-colUnit purchase-order-print-right">
                UNIT PRICE
              </th>
              <th className="purchase-order-print-colTotal purchase-order-print-right">
                AMOUNT
              </th>
            </tr>
          </thead>

          <tbody>
            {lines.map((line) => (
              <tr key={line.id}>
                <td>{safe(line.qty)}</td>
                <td>{safe(line.description)}</td>
                <td className="purchase-order-print-right">
                  {money(line.unitPrice)}
                </td>
                <td className="purchase-order-print-right purchase-order-print-strong">
                  {money(line.total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="purchase-order-print-flexSpacer" />

        <div className="purchase-order-print-totalsSlim">
          <div className="purchase-order-print-totalsHead">
            <div>SUBTOTAL</div>
            <div>VAT ({vatPct}%)</div>
            <div>DISCOUNT</div>
            <div>TOTAL</div>
          </div>

          <div className="purchase-order-print-totalsValues">
            <div>{money(subtotal)}</div>
            <div>{money(vat)}</div>
            <div>{money(disc)}</div>
            <div className="purchase-order-print-totalsTotal">{money(total)}</div>
          </div>
        </div>

        <div className="purchase-order-print-footNote">
          Please process this purchase order according to the agreed terms.
          Thank you for working with <strong>{iName}</strong>.
        </div>
      </div>
    );
  }
);

export default PurchaseOrderPrint;